import React, { useMemo } from 'react'
import Link from 'next/link'
import Slider from 'react-slick'
import cn from 'classnames'
import type { Settings as ReactSlickSettings } from 'react-slick'
import ArrowLeftCircle from '@components/icons/ArrowLeftCircle'
import ArrowRightCircle from '@components/icons/ArrowRightCircle'
import type { CategoryItem } from '@pylot/api/operations/get-categories-slider'
import { SLIDER_SETTINGS } from '@pylot/constants/plp'
import s from '@pagestyles/Plp.module.scss'

interface CategorySliderProps {
    sliderData: { CategorySlider?: { categoryItems?: CategoryItem[] } } | null
    className?: string
}

function PrevArrow({ onClick, currentSlide }: any) {
    if (currentSlide === 0) return null
    return (
        <button onClick={onClick} className={cn(s['arrow-button'], s['prev'])}>
            <ArrowLeftCircle />
        </button>
    )
}

function NextArrow({ onClick }: any) {
    if (!onClick) return null
    return (
        <button onClick={onClick} className={cn(s['arrow-button'], s['next'])}>
            <ArrowRightCircle />
        </button>
    )
}

export const CategorySlider: React.FC<CategorySliderProps> = ({
    sliderData,
    className
}) => {
    const categoryItems = sliderData?.CategorySlider?.categoryItems ?? []

    const sliderSettings: ReactSlickSettings = useMemo(() => {
        const slidesToShow =
            categoryItems?.length >= 5
                ? SLIDER_SETTINGS.LARGE_CATEGORY_SLIDES
                : SLIDER_SETTINGS.DEFAULT_SLIDES_TO_SHOW

        return {
            className: 'botonera-category-carousel',
            arrows: true,
            speed: 400,
            slidesToScroll: 1,
            slidesToShow,
            draggable: true,
            infinite: false,
            dots: false,
            fade: false,
            prevArrow: <PrevArrow />,
            nextArrow: <NextArrow />,
            responsive: [
                {
                    breakpoint: 1023,
                    settings: { slidesToShow: SLIDER_SETTINGS.TABLET_SLIDES }
                },
                {
                    breakpoint: 767,
                    settings: {
                        slidesToShow:
                            categoryItems.length >= 8
                                ? SLIDER_SETTINGS.MOBILE_SLIDES_LARGE
                                : SLIDER_SETTINGS.MOBILE_SLIDES_SMALL,
                        slidesToScroll: 1
                    }
                }
            ]
        }
    }, [categoryItems.length])

    if (!categoryItems?.length) return null

    return (
        <div className={cn(s['categories-slider'], className)}>
            <Slider {...sliderSettings}>
                {categoryItems.map((item: CategoryItem) => (
                    <Link
                        key={item.category_id}
                        href={`/categoria${item.url}`}
                        className={s['slider-square']}
                    >
                        <div
                            className={cn(s['slider-item'], s['slider-square'])}
                            style={{
                                background: `url(${item.imageUrl}) no-repeat center / cover`,
                                border: item.current
                                    ? '1.5px #EEE0B9 solid'
                                    : 'none'
                            }}
                        >
                            {!item.hide_name_in_slider && (
                                <h5
                                    className={cn(s['name'], {
                                        [s['card-selected']]: item.current
                                    })}
                                >
                                    {item.name}
                                </h5>
                            )}
                        </div>
                    </Link>
                ))}
            </Slider>
        </div>
    )
}
